import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity } from 'react-native'
import { Button, Text, Spinner, Toggle, Icon, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveProductModifiers, deleteProductModifiers, checkIdUniquenes } from '../../global/Firebase/firebase'
import { rethrowUnlessWRefusal } from '../../global/Write/writeW'
import HeaderIconButton from '../../components/HeaderIconButton'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { constantsAtom, productsDictAtom, storesDictAtom } from '../../global/AtomsProvider'

export default function ProductModifiersDetailScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const { detail, detailIndex, allData } = route.params
  const productsDict = useAtomValue(productsDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const constants = useAtomValue(constantsAtom)
  const [id, setId] = useState(detail ? (detail.id || '') : ('productModifier-' + Date.now()))
  const [name, setName] = useState(detail ? (detail.name || '') : '')
  const [data, setData] = useState(detail ? (detail.data || []) : [])
  const [activeInStore, setActiveInStore] = useState(detail ? (detail.activeInStore || {}) : {})
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            deleteProductModifiers(context.db, detailIndex, allData).catch(rethrowUnlessWRefusal) // P9b review 1: a W refusal was already announced
            navigation.goBack()
          }}
          iconName='trash-outline'
        />
      ),
      title: (detail?.name || '')
    })
  }, [])

  async function save() {
    setIsLoading(true)
    let uniqueId = true
    if (detailIndex == allData.length) {
      uniqueId = checkIdUniquenes(id, constants)
    }
    if (name !== '' && id !== '' && uniqueId) {
      setSaveError('')
      let info = {
        name,
        id,
        activeInStore,
        data,
      }
      try {
        await saveProductModifiers(context.db, info, detailIndex, allData)
        setSuccessMessageVisible(true)
        setIsLoading(false)
      } catch (error) {
        Sentry.captureException(error)
        console.log(error)
        setSaveError(t('error'))
        setIsLoading(false)
      }
    } else if (uniqueId) {
      setSaveError(t('notAllFieldsFilled'))
      setIsLoading(false)
    } else {
      setSaveError(t('settings.uniqueIdError'))
      setIsLoading(false)
    }
  }

  const setItemQuantity = (index, text) => {
    var newArr = [...data]
    var oldItem = newArr[index]
    oldItem.quantity = parseFloat(text)
    newArr[index] = oldItem
    setData(newArr)
  }

  const navigateToAddIngredients = () => {
    navigation.navigate('AddIngredientToProduct', {data, setData})
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail.id}
        </Text>}
    
        <Text category='label' style={styles.label}>
          {t('settings.name')}
        </Text>
        <StyledTextInput 
          value={name}
          onChangeText={text => setName(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />

        <Text category='label' style={styles.label}>
          {t('settings.settingsByStore')}  
        </Text>
        { Object.entries(storesDict).map(([key, value]) => (
          <View key={key} style={{marginBottom: 10}}>
            <Toggle 
              checked={activeInStore[key]}
              onChange={(value) => {
                var newActive = {...activeInStore}
                newActive[key] = value
                setActiveInStore(newActive)
              }}
              style={styles.toggleField}
            >
              {storesDict?.[key].name}
            </Toggle>
          </View>
          ))
        }

        <View style={{backgroundColor: theme['background-basic-color-1'], padding: 15, borderRadius: 10, marginBottom: 15}}>
          <View style={{flexDirection: 'row', alignItems: 'center', marginVertical: 3}}>
            <Text category='label'>{t('settings.modifiers')}</Text>
            <Button appearance='ghost' onPress={navigateToAddIngredients} accessoryLeft={(style) => (
              <Icon {...style} name='plus-outline'/>
            )}/>
          </View>
          { data.map((val, idx) => (
            <View style={{flexDirection: 'row', alignItems: 'center', marginVertical: 3}} key={val+idx}>
              <Text style={{width: 200, marginRight: 10}}>{productsDict[val.id]?.name}</Text>
              <StyledTextInput 
                style={{width: 100, marginRight: 10}}
                value={val.quantity.toString()}
                onChangeText={text => setItemQuantity(idx, text.replace(/,/g, '.'))}
                keyboardType='decimal-pad'
                returnKeyType='done'
              />
              <Text style={{width: 80}}>{(parseFloat(val.quantity) * productsDict[val.id]?.salePrices[context.user.store.id])?.toFixed(2)}</Text>
              <Button appearance='ghost' status='danger' accessoryLeft={(style) => (
                <Icon {...style} name='trash-outline'/>
              )} onPress={() => {
                var newIngredients = [...data]
                newIngredients.splice(idx, 1)
                setData(newIngredients)
              }} />
            </View>
          ))}
        </View>
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
          {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  toggleField: {
    marginBottom: 5,
    alignSelf: 'flex-start'
  },
});